import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  isRedirect,
  Link,
  lazyRouteComponent,
  notFound,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { api, isApiError } from '@/api/client';
import { queryClient } from '@/api/query-client';
import { sessionQuery } from '@/api/session';
import { AppShell } from '@/components/layout/app-shell';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/states';
import { AGENT_TABS } from '@/features/agents/tabs';
import {
  campaignEditSearchSchema,
  campaignNewSearchSchema,
  campaignsListSearchSchema,
} from '@/features/campaigns/editor-steps';
import { conversationDetailSearchSchema, conversationsSearchSchema } from '@/features/conversations/search';
import { inboxSearchSchema } from '@/features/inbox/search';
import { TENANT_DETAIL_TABS } from '@/features/platform/tenants/tabs';
import { portalSearchSchema } from '@/features/portal/search';
import { requestsSearchSchema } from '@/features/requests/search';
import { safeRedirect } from '@/lib/redirect';
import { sessionGate } from '@/lib/session-gate';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: () => (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 text-center">
      <p className="text-5xl font-semibold text-primary">404</p>
      <p className="text-muted-foreground">Esta página no existe.</p>
      <Button asChild>
        <Link to="/">Volver al inicio</Link>
      </Button>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto mt-24 max-w-lg p-6">
      <ErrorState error={error} onRetry={reset} title="Algo salió mal en esta pantalla" />
    </div>
  ),
});

/* ---------------- Login ---------------- */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    // Con sesión vigente no tiene sentido ver el login.
    const session = await context.queryClient.fetchQuery({ ...sessionQuery, staleTime: 0 }).catch(() => null);
    if (session) throw redirect({ to: safeRedirect(search.redirect) });
  },
  component: lazyRouteComponent(() => import('@/features/auth/login-page'), 'LoginPage'),
});

/* ---------------- Cambio obligatorio de contraseña temporal ---------------- */
const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  beforeLoad: async ({ context, location }) => {
    try {
      const session = await context.queryClient.ensureQueryData(sessionQuery);
      // Con el 2FA por verificar la API no deja cambiar la contraseña: primero el segundo factor.
      if (session.mfa.state === 'verify') throw redirect({ to: '/two-factor' });
      if (!session.user.mustChangePassword) throw redirect({ to: '/' });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isApiError(e) && e.status === 401) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    }
  },
  component: lazyRouteComponent(() => import('@/features/auth/change-password-page'), 'ChangePasswordPage'),
});

/* ---------------- Segundo factor (2FA) y recuperación de contraseña ---------------- */
// Verificación / activación obligatoria del 2FA tras el login. Con la sesión resuelta (`mfa.state === 'ok'`) no tiene sentido verla.
const twoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/two-factor',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, location }) => {
    try {
      const session = await context.queryClient.ensureQueryData(sessionQuery);
      if (session.mfa.state === 'ok') throw redirect({ to: '/' });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isApiError(e) && e.status === 401) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    }
  },
  component: lazyRouteComponent(() => import('@/features/auth/two-factor-page'), 'TwoFactorPage'),
});

// Seguridad de la cuenta (2FA opcional/obligatorio, códigos de recuperación). Accesible para cualquier usuario con sesión resuelta.
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  beforeLoad: async ({ context, location }) => {
    try {
      const session = await context.queryClient.ensureQueryData(sessionQuery);
      const gate = sessionGate(session);
      if (gate) throw redirect({ to: gate });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isApiError(e) && e.status === 401) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    }
  },
  component: lazyRouteComponent(() => import('@/features/auth/security-page'), 'SecurityPage'),
});

// Recuperación de contraseña: públicas (sin sesión). El enlace del correo abre `/reset-password#token=…`: solo muestra la pantalla; el cambio es un POST.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('@/features/auth/forgot-password-page'), 'ForgotPasswordPage'),
});
// Aceptar una invitación a un tenant (ADR-0016): pública. El enlace del correo trae el token en el FRAGMENTO (`#token=…`); abrirla no cambia nada.
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  component: lazyRouteComponent(() => import('@/features/auth/accept-invitation-page'), 'AcceptInvitationPage'),
});
// Autoservicio SaaS (PR-11): catálogo público de planes, registro y verificación del correo. Públicas: no piden sesión. El enlace del correo trae el token en el FRAGMENTO.
// Responder a la propuesta de otra hora (ADR-0021): pública. El enlace del correo trae el token en el FRAGMENTO; abrirla no cambia nada.
const appointmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appointment',
  component: lazyRouteComponent(() => import('@/features/appointments/appointment-page'), 'AppointmentPage'),
});
// Pago SIMULADO de entradas (ADR-0022): pública. El enlace que entrega el agente trae el token en el FRAGMENTO; abrirla no cambia nada.
const payRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay',
  component: lazyRouteComponent(() => import('@/features/events/pay-page'), 'PayPage'),
});
// Confirmar o cancelar una reserva de mesa (ADR-0024): pública. El enlace del correo trae el token en el FRAGMENTO; abrirla no cambia nada.
const reservationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reservation',
  component: lazyRouteComponent(() => import('@/features/tables/reservation-page'), 'ReservationPage'),
});
// Valorar la experiencia (ADR-0027): pública. El enlace del correo trae el token en el FRAGMENTO; abrirla no cambia nada.
const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/feedback',
  component: lazyRouteComponent(() => import('@/features/feedback/feedback-page'), 'FeedbackPage'),
});
const publicPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('@/features/subscription/public-plans-page'), 'PublicPlansPage'),
});
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: z.object({ plan: z.string().optional() }),
  component: lazyRouteComponent(() => import('@/features/subscription/signup-page'), 'SignupPage'),
});
const verifySignupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-signup',
  component: lazyRouteComponent(() => import('@/features/subscription/verify-signup-page'), 'VerifySignupPage'),
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: lazyRouteComponent(() => import('@/features/auth/reset-password-page'), 'ResetPasswordPage'),
});

/* ---------------- Área autenticada ---------------- */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      const session = await context.queryClient.ensureQueryData(sessionQuery);
      // Segundo factor por verificar/enrolar o contraseña temporal (alta/reseteo por plataforma): la API bloquea todo lo demás; la UI lleva directo a resolverlo.
      const gate = sessionGate(session);
      if (gate) throw redirect({ to: gate });
      // El panel admin es solo para administradores de plataforma: los usuarios de un cliente viven en el portal (`/portal`).
      // Guarda de UX; la API vuelve a autorizar cada ruta.
      if (!session.user.isPlatformAdmin) throw redirect({ to: '/portal' });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isApiError(e) && e.status === 401) {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: AppShell,
});

const tenantSearch = z.object({ tenant: z.string().optional() });

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  validateSearch: tenantSearch,
  // El platform admin no tiene «Inicio» propio: su página de inicio es el Resumen de plataforma.
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session.user.isPlatformAdmin) throw redirect({ to: '/platform' });
  },
  component: lazyRouteComponent(() => import('@/features/home/home-page'), 'HomePage'),
});

const demosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/demos',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/demos/demos-page'), 'DemosPage'),
});

const demoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/demos/$agentId',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/demos/demo-screen'), 'DemoScreen'),
});

const tenantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants',
  validateSearch: z.object({
    new: z.boolean().optional(),
    q: z.string().optional(),
    status: z.enum(['active', 'suspended']).optional(),
    vertical: z.string().optional(),
    demo: z.enum(['yes', 'no']).optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/tenants/tenants-page'), 'TenantsPage'),
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId',
  validateSearch: z.object({
    tab: z.enum(['summary', 'agents', 'members', 'costs', 'budget']).optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/tenants/tenant-detail-page'), 'TenantDetailPage'),
});

const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/agents/agents-page'), 'AgentsPage'),
});

const agentNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents/new',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/agents/agent-editor-page'), 'AgentCreatePage'),
});

const agentEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents/$agentId',
  validateSearch: z.object({ tenant: z.string().optional(), tab: z.enum(AGENT_TABS).optional() }),
  component: lazyRouteComponent(() => import('@/features/agents/agent-editor-page'), 'AgentEditPage'),
});

const channelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/channels/channels-page'), 'ChannelsPage'),
});

const fidelizadorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fidelizador',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/fidelizador/fidelizador-page'), 'FidelizadorPage'),
});

/* Campañas de correo (ADR-0025 F4): lista, editor por pasos y resultados. Solo con el interruptor `fidelizador.campaigns` encendido (la pantalla lo explica si no). */
const campaignsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns',
  validateSearch: campaignsListSearchSchema,
  component: lazyRouteComponent(() => import('@/features/campaigns/campaigns-page'), 'CampaignsPage'),
});

// Valoraciones posteriores a la experiencia (ADR-0027): promedios y respuestas, solo lectura.
const experienceFeedbackRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/experience-feedback',
  component: lazyRouteComponent(() => import('@/features/feedback/feedback-results-page'), 'FeedbackResultsPage'),
});

const campaignNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/new',
  validateSearch: campaignNewSearchSchema,
  component: lazyRouteComponent(() => import('@/features/campaigns/campaign-editor-page'), 'CampaignNewPage'),
});

/* Disparadores automáticos (F5a): tres reglas apagadas por defecto; owner/admin las encienden. */
const campaignTriggersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/triggers',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/campaigns/triggers-page'), 'TriggersPage'),
});

const campaignEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/$campaignId',
  validateSearch: campaignEditSearchSchema,
  component: lazyRouteComponent(() => import('@/features/campaigns/campaign-editor-page'), 'CampaignEditPage'),
});

const campaignResultsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/$campaignId/results',
  validateSearch: tenantSearch,
  component: lazyRouteComponent(() => import('@/features/campaigns/campaign-results-page'), 'CampaignResultsPage'),
});

/* Visor de conversaciones de Martech: lista con filtros + detalle técnico (turnos, herramientas, costo exacto). */
const conversationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conversations',
  validateSearch: conversationsSearchSchema,
  component: lazyRouteComponent(() => import('@/features/conversations/conversations-page'), 'ConversationsPage'),
});

const conversationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conversations/$conversationId',
  validateSearch: conversationDetailSearchSchema,
  component: lazyRouteComponent(
    () => import('@/features/conversations/conversation-detail-page'),
    'ConversationDetailPage',
  ),
});

/* Bandeja: conversaciones que esperan a una persona (tomar, responder por el mismo canal, devolver al agente). */
const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  validateSearch: inboxSearchSchema,
  component: lazyRouteComponent(() => import('@/features/inbox/inbox-page'), 'InboxPage'),
});

/* Solicitudes del equipo (ADR-0021): lo que el agente dejó para que una persona actúe (cotización, hora de taller, repuesto, reclamo). */
const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/requests',
  validateSearch: requestsSearchSchema,
  component: lazyRouteComponent(() => import('@/features/requests/requests-page'), 'RequestsPage'),
});

const costsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/costs',
  validateSearch: z.object({
    tenant: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    groupBy: z.enum(['day', 'agent', 'model', 'channel', 'purpose', 'kind']).optional(),
    view: z.enum(['tenant', 'platform']).optional(),
    demos: z.enum(['include', 'exclude']).optional(),
    conversation: z.string().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/costs/costs-page'), 'CostsPage'),
});

const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analytics',
  validateSearch: z.object({
    tenant: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    tz: z.string().optional(),
    agent: z.string().optional(),
    channel: z.enum(['widget', 'whatsapp', 'instagram', 'simulator', 'api']).optional(),
    demos: z.enum(['include', 'exclude']).optional(),
    outcome: z.enum(['lead', 'appointment', 'handoff', 'none']).optional(),
    status: z.enum(['open', 'closed', 'handoff']).optional(),
    conversation: z.string().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/analytics/analytics-page'), 'AnalyticsPage'),
});

/* ---------------- Plataforma (solo platform_admin) ---------------- */
// Guarda en `beforeLoad` del padre: TODA ruta hija la hereda. Quien no es platform admin recibe el 404 genérico (no se filtra que existe).
// La API vuelve a exigir `platform:manage` en cada ruta (deny by default): esta guarda es solo UX.
const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform',
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session.user.isPlatformAdmin) throw notFound();
  },
  component: Outlet,
});
const platformIndexRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/features/platform/overview/overview-page'), 'PlatformOverviewPage'),
});
const platformTenantsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/tenants',
  validateSearch: z.object({
    new: z.boolean().optional(),
    q: z.string().optional(),
    status: z.enum(['active', 'suspended', 'archived']).optional(),
    demo: z.enum(['yes', 'no']).optional(),
  }),
  component: lazyRouteComponent(
    () => import('@/features/platform/tenants/platform-tenants-page'),
    'PlatformTenantsPage',
  ),
});
const platformTenantRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/tenants/$tenantId',
  validateSearch: z.object({
    tab: z.enum(TENANT_DETAIL_TABS).optional(),
  }),
  component: lazyRouteComponent(
    () => import('@/features/platform/tenants/platform-tenant-detail-page'),
    'PlatformTenantDetailPage',
  ),
});
const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/users',
  validateSearch: z.object({
    q: z.string().optional(),
    status: z.enum(['active', 'disabled']).optional(),
    tenant: z.string().optional(),
    user: z.string().optional(),
    new: z.boolean().optional(),
    newAdmin: z.boolean().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/platform/users/platform-users-page'), 'PlatformUsersPage'),
});
const platformCostsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/costs',
  validateSearch: z.object({
    tab: z.enum(['report', 'margins']).optional(),
    currency: z.enum(['USD', 'CLP']).optional().catch(undefined),
    from: z.string().optional(),
    to: z.string().optional(),
    groupBy: z.enum(['tenant', 'agent', 'provider', 'model', 'channel', 'kind', 'purpose', 'day']).optional(),
    demos: z.enum(['include', 'exclude']).optional(),
    tenant: z.string().optional(),
    month: z.string().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/platform/costs/platform-costs-page'), 'PlatformCostsPage'),
});
const platformPricesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/prices',
  validateSearch: z.object({
    kind: z.enum(['llm', 'messaging']).optional(),
    provider: z.string().optional(),
    q: z.string().optional(),
    status: z.enum(['current', 'scheduled', 'superseded']).optional(),
    new: z.boolean().optional(),
    /** Pestaña: `precios` (catálogo, por omisión) o `cambio` (tipo de cambio USD → CLP del portal). */
    tab: z.enum(['precios', 'cambio']).optional().catch(undefined),
    /** Abre el formulario «Cargar nueva tasa» de la pestaña de tipo de cambio. */
    newFx: z.boolean().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/platform/prices/platform-prices-page'), 'PlatformPricesPage'),
});
const platformBudgetsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/budgets',
  validateSearch: z.object({
    q: z.string().optional(),
    filter: z.enum(['all', 'alerts', 'exceeded', 'none']).optional(),
    tenant: z.string().optional(),
  }),
  component: lazyRouteComponent(
    () => import('@/features/platform/budgets/platform-budgets-page'),
    'PlatformBudgetsPage',
  ),
});
const platformOperationsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/operations',
  validateSearch: z.object({
    tab: z.enum(['queues', 'whatsapp', 'webhooks', 'messages', 'mail']).optional(),
    queue: z.enum(['channel-inbound', 'channel-outbound', 'channel-status', 'maintenance', 'ai-functions']).optional(),
    kind: z.enum(['failed', 'stuck']).optional(),
  }),
  component: lazyRouteComponent(
    () => import('@/features/platform/operations/platform-operations-page'),
    'PlatformOperationsPage',
  ),
});
const platformAuditRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/audit',
  validateSearch: z.object({
    tenant: z.string().optional(),
    actor: z.string().optional(),
    action: z.string().optional(),
    entity: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    global: z.boolean().optional(),
  }),
  component: lazyRouteComponent(() => import('@/features/platform/audit/platform-audit-page'), 'PlatformAuditPage'),
});
const platformFidelizadorRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/fidelizador',
  validateSearch: z.object({ tenant: z.string().optional() }),
  component: lazyRouteComponent(
    () => import('@/features/platform/fidelizador/platform-fidelizador-page'),
    'PlatformFidelizadorPage',
  ),
});
const platformServicesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/services',
  // `service` = código del servicio abierto en el sheet de edición (compartible por URL).
  validateSearch: z.object({ service: z.string().optional() }),
  component: lazyRouteComponent(
    () => import('@/features/platform/services/platform-services-page'),
    'PlatformServicesPage',
  ),
});
const platformPlansRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('@/features/platform/pricing/platform-plans-page'), 'PlatformPlansPage'),
});
const platformSubscriptionsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/subscriptions',
  // `status` = filtro por estado; `tenant` = suscripción abierta en el panel lateral (compartibles por URL).
  validateSearch: z.object({
    status: z.enum(['trial', 'pending_payment', 'active', 'past_due', 'paused', 'cancelled']).optional(),
    tenant: z.string().optional(),
  }),
  component: lazyRouteComponent(
    () => import('@/features/platform/subscriptions/platform-subscriptions-page'),
    'PlatformSubscriptionsPage',
  ),
});
const platformAiRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/ai',
  component: lazyRouteComponent(() => import('@/features/platform/ai/platform-ai-page'), 'PlatformAiPage'),
});

/* ---------------- Portal del cliente (solo lectura; cualquier usuario con sesión) ---------------- */
// Layout propio (sin menú admin ni «Plataforma»). La API autoriza por membresía; el platform admin entra con «Ver portal como cliente».
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  validateSearch: portalSearchSchema,
  beforeLoad: async ({ context, location }) => {
    try {
      const session = await context.queryClient.ensureQueryData(sessionQuery);
      const gate = sessionGate(session);
      if (gate) throw redirect({ to: gate });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isApiError(e) && e.status === 401) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    }
  },
  component: lazyRouteComponent(() => import('@/features/portal/portal-shell'), 'PortalShell'),
});
const portalIndexRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/features/portal/summary-page'), 'SummaryPage'),
});
const portalAnalyticsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/analytics',
  component: lazyRouteComponent(() => import('@/features/portal/analytics-page'), 'PortalAnalyticsPage'),
});
const portalConversationsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/conversations',
  component: lazyRouteComponent(() => import('@/features/portal/conversations-page'), 'PortalConversationsPage'),
});
const portalInboxRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/inbox',
  component: lazyRouteComponent(() => import('@/features/portal/inbox-page'), 'PortalInboxPage'),
});
const portalRequestsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/requests',
  component: lazyRouteComponent(() => import('@/features/portal/requests-page'), 'PortalRequestsPage'),
});
const portalSpendRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/spend',
  component: lazyRouteComponent(() => import('@/features/portal/spend-page'), 'SpendPage'),
});
const portalLeadsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/leads',
  component: lazyRouteComponent(() => import('@/features/portal/leads-page'), 'PortalLeadsPage'),
});
const portalChannelsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/channels',
  component: lazyRouteComponent(() => import('@/features/portal/channels-page'), 'PortalChannelsPage'),
});
const portalMembersRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/members',
  component: lazyRouteComponent(() => import('@/features/portal/members-page'), 'PortalMembersPage'),
});
const portalPrivacyRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/privacy',
  component: lazyRouteComponent(() => import('@/features/portal/privacy-page'), 'PortalPrivacyPage'),
});
const portalSubscriptionRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/subscription',
  component: lazyRouteComponent(() => import('@/features/portal/subscription-page'), 'PortalSubscriptionPage'),
});
const portalCheckoutRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/checkout',
  component: lazyRouteComponent(() => import('@/features/portal/checkout-page'), 'PortalCheckoutPage'),
});
const portalAccountRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/account',
  component: lazyRouteComponent(() => import('@/features/portal/account-page'), 'PortalAccountPage'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  changePasswordRoute,
  twoFactorRoute,
  securityRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInvitationRoute,
  appointmentRoute,
  payRoute,
  reservationRoute,
  feedbackRoute,
  publicPlansRoute,
  signupRoute,
  verifySignupRoute,
  portalRoute.addChildren([
    portalIndexRoute,
    portalAnalyticsRoute,
    portalConversationsRoute,
    portalInboxRoute,
    portalRequestsRoute,
    portalLeadsRoute,
    portalSpendRoute,
    portalChannelsRoute,
    portalMembersRoute,
    portalPrivacyRoute,
    portalSubscriptionRoute,
    portalCheckoutRoute,
    portalAccountRoute,
  ]),
  appRoute.addChildren([
    homeRoute,
    demosRoute,
    demoRoute,
    tenantsRoute,
    tenantDetailRoute,
    agentsRoute,
    agentNewRoute,
    agentEditRoute,
    channelsRoute,
    fidelizadorRoute,
    campaignsRoute,
    experienceFeedbackRoute,
    campaignNewRoute,
    campaignTriggersRoute,
    campaignEditRoute,
    campaignResultsRoute,
    conversationsRoute,
    conversationDetailRoute,
    inboxRoute,
    requestsRoute,
    analyticsRoute,
    costsRoute,
    platformRoute.addChildren([
      platformIndexRoute,
      platformTenantsRoute,
      platformTenantRoute,
      platformUsersRoute,
      platformCostsRoute,
      platformPricesRoute,
      platformBudgetsRoute,
      platformOperationsRoute,
      platformAuditRoute,
      platformServicesRoute,
      platformFidelizadorRoute,
      platformPlansRoute,
      platformSubscriptionsRoute,
      platformAiRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Un 401 en cualquier llamada (sesión expirada/revocada) devuelve al login conservando la ruta.
api.setOnUnauthorized(() => {
  queryClient.removeQueries({ queryKey: ['session'] });
  const { pathname, href } = router.state.location;
  if (pathname !== '/login') void router.navigate({ to: '/login', search: { redirect: href } });
});
